import React from 'react';

// validOverride: when provided, use this boolean to display validation UI instead of internal check
export default function TextField({ id, label, value, onChange, type = 'text', required, placeholder, validOverride }) {
  // We support both single-line text inputs and multi-line text areas.
  // - When type is "text" (default), render a standard <input>.
  // - When type is "textArea" (schema-driven), render a <textarea> with 4 rows by default.
  //   This gives users more visible space for longer answers without changing any other logic.
  const normalizedType = (type || 'text');
  const isTextArea = String(normalizedType).toLowerCase() === 'textarea';
  const isEmail = normalizedType === 'email';
  // For long-answer questions (textArea), product wants a generic prompt instead of repeating the question text.
  // We still receive the original question label via props for accessibility/analytics upstream if needed,
  // but the on-screen field label shown here is standardized.
  const displayedLabel = isTextArea ? 'Please answer below.' : label;
  // Minimal fallback only if no override provided
  const fallbackEmailPattern = /^\S+@\S+\.\S+$/;
  const isValid = validOverride !== undefined ? !!validOverride : (!isEmail || !value || fallbackEmailPattern.test(value));
  const showEmailError = isEmail && !isValid && !!value;
  return (
    <div className="m-1 mb-4">
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
        {displayedLabel}{required ? <span className="text-red-600"> *</span> : null}
      </label>
      {isTextArea ? (
        <textarea
          id={id}
          rows={4}
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          className={`w-full p-3 border-2 rounded-lg bg-white text-gray-900 text-base placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${isValid ? 'border-gray-200' : 'border-red-300'}`}
        />
      ) : (
        <input
          id={id}
          type={normalizedType}
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          aria-invalid={showEmailError ? true : undefined}
          aria-describedby={showEmailError && id ? `${id}-error` : undefined}
          className={`w-full p-3 border-2 rounded-lg bg-white text-gray-900 text-base placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${isValid ? 'border-gray-200' : 'border-red-300'}`}
        />
      )}
      {/* Email fields only, and out of the accessibility tree until shown,
          so a screen reader never reads an email error on another question. */}
      {isEmail && (
        <div
          className={`transition-all duration-300 overflow-hidden ${showEmailError ? 'max-h-6 mt-1' : 'max-h-0 mt-0'}`}
          aria-hidden={showEmailError ? undefined : true}
        >
          <div id={id ? `${id}-error` : undefined} className={`text-xs text-red-600 ${showEmailError ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}>
            Please enter a valid email address.
          </div>
        </div>
      )}
    </div>
  );
}


